/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Thumbnail grid geometry, as pure functions.
 *
 * Everything here answers one of three questions:
 *   - which domain is this position in?          (toFrameEpochDomain)
 *   - which grid slot does it belong to?         (snapToStep)
 *   - which CDN file is that slot?               (thumbnailFilenameFor / buildThumbnailUrl)
 *
 * These carry the subtlest history in the thumbnail subsystem — the
 * mStartTime-anchored grid, magnitude-based scale detection, and the
 * mobi filename convention — and each has caused a silent
 * every-lookup-misses regression at least once. Pulled out of
 * ThumbnailManager so they can be tested directly against their edge cases
 * rather than inferred from fetch counts several layers up.
 *
 * Deliberately functions over a class: the manager already owns the geometry
 * STATE (start, end, interval, template) and reads those fields for
 * non-geometry purposes too, so moving ownership here would only add an
 * accessor hop at fifty-odd call sites. What was worth extracting is the
 * arithmetic, not the storage.
 */

/** Seconds per media segment (mobi filename convention). */
export const MEDIA_SEGMENT_DURATION: number = 6;

/** Seconds between consecutive thumbnails (2 × MEDIA_SEGMENT_DURATION). */
export const THUMBNAIL_INTERVAL: number = MEDIA_SEGMENT_DURATION * 2;

/** Number of media segments covered by each thumbnail (used as filename step). */
export const THUMBNAIL_FILENAME_STEP: number = 2;

/**
 * Magnitude boundary (seconds) separating wall-clock epoch positions
 * (~1.7e9, year 2024+) from content-relative 0-based positions (0…duration).
 * 1e8 ≈ year 1973 in epoch seconds: no real content duration approaches it and
 * every real epoch far exceeds it.
 */
export const EPOCH_SCALE_THRESHOLD_SEC: number = 100000000;

/**
 * Convert a raw player position into the frame-epoch domain — the domain the
 * cache keys, snap grid and CDN filenames all use.
 *
 * Scale is detected by MAGNITUDE rather than the (unreliable) live flag,
 * because the player clock's scale varies by category and was the source of
 * repeated cache-miss regressions:
 *   - Wall-clock epoch positions (LIVE presentation time, and any category
 *     whose player clock is epoch-aligned) are ~1.7e9 — already in the
 *     cache-key domain — so they pass through unchanged.
 *   - Content-relative 0-based positions (VOD/SOCU/CatchUp/NDVR, 0…duration)
 *     are shifted by startTime to land on the frame's airing epoch.
 *
 * Keeping the prefetch and lookup paths on one conversion is what makes their
 * cache keys line up regardless of the per-category quirks in how the View
 * layer reports positions.
 *
 * @param playerPosition - Raw position from the player.
 * @param startTime      - Content start epoch (seconds).
 */
export function toFrameEpochDomain(
  playerPosition: number,
  startTime: number
): number {
  if (playerPosition >= EPOCH_SCALE_THRESHOLD_SEC) {
    return playerPosition;
  }
  return startTime + playerPosition;
}

/**
 * Round an absolute position to the nearest multiple of `stepSeconds` measured
 * from `startTime`, floored at `startTime`.
 *
 * The single rounding primitive behind both grids — the fine lookup grid
 * (stepSeconds = interval) and the coarse prefetch sub-lattice (stepSeconds =
 * interval × stride).
 *
 * Anchoring to startTime is critical and is NOT interchangeable with rounding
 * to global multiples of the step: thumbnails exist at startTime + k × interval,
 * so a global grid produces keys that exist in no map whenever
 * startTime % interval !== 0 — every exact lookup and every neighbour probe
 * then misses, silently, because the last-known-good fallback just holds the
 * previous frame.
 *
 * GUARANTEE: the result is always >= startTime. Callers rely on this and apply
 * an upper bound only.
 *
 * @param absolutePositionSeconds - Position in the frame-epoch domain.
 * @param startTime               - Grid anchor (content start epoch).
 * @param stepSeconds             - Grid spacing in seconds (> 0).
 */
export function snapToStep(
  absolutePositionSeconds: number,
  startTime: number,
  stepSeconds: number
): number {
  const relative = absolutePositionSeconds - startTime;
  const remainder = relative % stepSeconds;
  let snappedRelative =
    remainder <= stepSeconds / 2
      ? relative - remainder
      : relative - remainder + stepSeconds;
  if (snappedRelative < 0) {
    snappedRelative = 0;
  }
  return startTime + snappedRelative;
}

/**
 * The CDN filename index for a frame at `offset`.
 *
 * mobi convention: floor(offset / MEDIA_SEGMENT_DURATION), forced odd. Because
 * consecutive thumbnails are exactly THUMBNAIL_FILENAME_STEP segments apart,
 * walking the grid and adding that step per entry yields the same sequence as
 * evaluating this per offset — which is why the eager entry builder and the
 * 10-foot URL builder can share it.
 *
 * @param offset - Absolute time in the frame-epoch domain.
 */
export function thumbnailFilenameFor(offset: number): number {
  const fileName = Math.floor(offset / MEDIA_SEGMENT_DURATION);
  return fileName % 2 === 0 ? fileName + 1 : fileName;
}

/**
 * Build the CDN URL for a frame at `offset`, or null when no template is set.
 *
 * @param urlTemplate - Template containing the `{FILENAME}` placeholder.
 * @param offset      - Absolute time in the frame-epoch domain.
 */
export function buildThumbnailUrl(
  urlTemplate: string,
  offset: number
): string | null {
  if (urlTemplate === "") {
    return null;
  }
  return urlTemplate.replace(
    "{FILENAME}",
    String(thumbnailFilenameFor(offset))
  );
}
